import {
  Component,
  OnInit,
  input,
  output,
  inject,
  ChangeDetectionStrategy,
} from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { TableFieldTypes } from "../../../locale/consts.en";
import {
  FormTableAggregateType,
  TableAggregateType,
  TableFieldCondition,
  TableFieldType,
} from "./table-field.model";
import { MasterDataService } from "../../master-data/master-data-service";
import { Observable } from "rxjs";
import {
  DECIMAL_PATTERN,
  INTEGER_PATTERN,
  ProductCategory,
} from "../../master-data/master-data.model";
import { FormTableType } from "../forms.model";
import { MatSelectChange } from "@angular/material/select";
import { FormsService } from "../forms.service";
import { first } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { TableFieldConditionDialogComponent } from "./table-filed-condition-dialog/table-field-condition-dialog.component";
import {
  DropdownEditValuesDialogComponent,
  DropdownEditValuesDialogData,
  DropdownValueItem,
} from "src/app/forms/table-field/dropdown-edit-values-dialog/dropdown-edit-values-dialog.component";

@Component({
  selector: "app-table-field",
  templateUrl: "./table-field.component.html",
  styleUrls: ["./table-field.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TableFieldComponent implements OnInit {
  private masterDataService = inject(MasterDataService);
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  formsService = inject(FormsService);

  readonly itemForm = input<FormGroup>(undefined);
  readonly index = input<number>(undefined);
  readonly tableType = input<FormTableType>(undefined);
  readonly deleteItem = output<number>();

  inputTypes = TableFieldTypes;
  TableFieldTypeT = TableFieldType;
  TableAggregateT = TableAggregateType;
  FormTableT = FormTableType;
  selectedType;

  productCategoriesMap$: Observable<Map<string, string>>;
  productCategories: ProductCategory[] = [];

  inputTmp = "";

  public expForm: FormGroup = this.fb.group({
    expression: [""],
  });

  ngOnInit(): void {
    if (this.itemForm().controls.columnKey.value === "productCategoryUuid") {
      /// read categories only in case of category column
      this.productCategoriesMap$ = this.masterDataService.productCategoriesMap$;
      this.masterDataService
        .getProductCategories()
        .pipe(first())
        .subscribe({
          next: (categories) => {
            this.productCategories = categories;
          },
        });
    }
    this.itemFormSetValues();
    this.itemFormSetValidator();
    this.itemFormDisableControls();
  }

  itemFormDisableControls() {
    const itemForm = this.itemForm();
    if (
      this.isDeviceTableRequiredColumn() ||
      itemForm.get("formTableAggregateType").value ===
        FormTableAggregateType.CNT ||
      itemForm.get("formTableAggregateType").value ===
        FormTableAggregateType.AVG
    ) {
      itemForm.get("required")?.disable();
      itemForm.get("type")?.disable();
      itemForm.get("defaultValue")?.enable();
    } else {
      itemForm.get("required")?.enable();
      itemForm.get("type")?.enable();
      if (itemForm.get("type").value === TableFieldType.DROPDOWN) {
        this.setDefaultOptionDisabledWhenNoInputs();
      } else {
        itemForm.get("defaultValue")?.enable();
      }
    }
  }

  itemFormSetValues() {
    const index = this.index();
    this.itemForm().controls.sort.setValue(index);
    let colName = "col_" + String.fromCharCode("a".charCodeAt(0) + index);
    if (this.tableType() === FormTableType.AGGREGATE) {
      colName = "dim_" + colName;
    }
    const itemForm = this.itemForm();
    if (
      itemForm.controls.columnKey.value == null ||
      itemForm.controls.columnKey.value === ""
    ) {
      itemForm.controls.columnKey.setValue(colName);
    }
    this.selectedType = +itemForm.controls.type.value;

    if (
      itemForm.controls.type.value === +TableFieldType.EXPRESSION &&
      (itemForm.controls.values as FormArray).length > 0
    ) {
      this.expForm.setValue({
        expression: (itemForm.controls.values as FormArray).controls[0].value,
      });
    }
  }

  itemFormSetValidator() {
    this.itemForm().setValidators(this.formHiddenAndDefaultValidator);
    this.itemForm().updateValueAndValidity();
  }

  formHiddenAndDefaultValidator(form: AbstractControl) {
    if (
      form.get("visible")?.value === false &&
      form.get("required")?.value === true &&
      form.get("defaultValue")?.value == null
    ) {
      return { validRequired: true };
    }
    return null;
  }

  delete() {
    this.deleteItem.emit(this.index());
  }

  changeType(value) {
    this.selectedType = value;
    const array = this.itemForm().controls.values as FormArray;
    const defaultValue = this.itemForm().controls.defaultValue as FormControl;
    array.clear();
    switch (value as TableFieldType) {
      case TableFieldType.INTEGER:
        defaultValue.setValidators(Validators.pattern(INTEGER_PATTERN));
        break;
      case TableFieldType.DECIMAL:
        defaultValue.setValidators(Validators.pattern(DECIMAL_PATTERN));
        break;
      case TableFieldType.DROPDOWN:
      case TableFieldType.RADIO:
      case TableFieldType.EXPRESSION:
        array.setValidators(Validators.required);
        break;
      default:
        array.setValidators(null);
        defaultValue.setValidators(null);
        break;
    }
    defaultValue.updateValueAndValidity();
    array.updateValueAndValidity();
  }

  getValues() {
    return (this.itemForm().get("values") as FormArray).controls;
  }

  onAddButtonClick() {
    if (this.inputTmp.length > 0) {
      const array = this.itemForm().controls.values as FormArray;
      array.push(new FormControl(this.inputTmp));
      this.inputTmp = "";
      this.setDefaultOptionDisabledWhenNoInputs();
    }
  }

  setDefaultOptionDisabledWhenNoInputs() {
    const vals = (this.itemForm().controls.values as FormArray).length;
    if (vals > 0) {
      this.itemForm().get("defaultValue")?.enable();
    } else {
      this.itemForm().get("defaultValue")?.disable();
    }
  }

  onTextChange(event) {
    this.inputTmp = event.target.value;
  }

  onExprChange(event) {
    const array = this.itemForm().controls.values as FormArray;
    array.clear();
    if (event.target.value !== "") {
      array.push(new FormControl(event.target.value));
    }
  }

  isDeviceTableColumn(): boolean {
    const val = this.itemForm().controls.columnKey.value;
    return val.indexOf("device") !== -1 || val.indexOf("product") !== -1;
  }

  isDeviceTableRequiredColumn(): boolean {
    const val = this.itemForm().controls.columnKey.value;
    return val === "productCategoryUuid" || val === "deviceProductUuid";
  }

  isFixedAggregateType(itemForm: FormGroup) {
    return (
      this.tableType() === FormTableType.AGGREGATE &&
      (+itemForm.controls.formTableAggregateType.value ===
        FormTableAggregateType.CNT ||
        +itemForm.controls.formTableAggregateType.value ===
          FormTableAggregateType.AVG ||
        itemForm.controls.formTableAggregateType.value == null ||
        itemForm.controls.aggregate == null ||
        itemForm.controls.aggregate.value === false)
    );
  }

  changeVisible(itemForm: FormGroup) {
    itemForm.controls.visible.setValue(!itemForm.controls.visible.value);
  }

  aggregateSelected(itemForm: FormGroup) {
    const colArr = itemForm.controls.columnKey.value.split("_");
    const colName = colArr.slice(1, colArr.length).join("_");
    if (itemForm.controls.aggregate.value) {
      let prefix =
        itemForm.controls.formTableAggregateType.value == null ||
        isNaN(itemForm.controls.formTableAggregateType.value)
          ? "agr"
          : TableAggregateType[
              itemForm.controls.formTableAggregateType.value
            ]?.toLowerCase();
      if (
        itemForm.controls.defaultValue.value != null &&
        itemForm.controls.defalutValue?.value !== ""
      ) {
        prefix = prefix + itemForm.controls.defaultValue.value;
      }
      prefix += "_";
      itemForm.controls.formTableAggregateType.setValidators([
        Validators.required,
      ]);
      itemForm.controls.columnKey.setValue(prefix + colName);
    } else {
      itemForm.controls.type.setValue(TableFieldType.TEXT);
      itemForm.controls.formTableAggregateType.setValue(null);
      itemForm.controls.formTableAggregateType.setValidators([]);
      let prefix = "dim";
      prefix +=
        itemForm.controls.defaultValue.value == null
          ? "_"
          : itemForm.controls.defaultValue.value + "_";
      itemForm.controls.columnKey.setValue(prefix + colName);
    }
    itemForm.controls.formTableAggregateType.updateValueAndValidity();
  }

  onAggregateTypeChanged(itemForm: FormGroup, $event: MatSelectChange) {
    const selAggr = $event.value;
    const colArr = itemForm.controls.columnKey.value.split("_");
    const colName = colArr.slice(1, colArr.length).join("_");
    let prefix =
      itemForm.controls.formTableAggregateType.value == null ||
      isNaN(itemForm.controls.formTableAggregateType.value)
        ? "agr"
        : TableAggregateType[
            itemForm.controls.formTableAggregateType.value
          ].toLowerCase();
    prefix +=
      itemForm.controls.defaultValue.value == null
        ? "_"
        : itemForm.controls.defaultValue.value + "_";
    if (
      +selAggr === FormTableAggregateType.CNT ||
      +selAggr === FormTableAggregateType.CND
    ) {
      itemForm.controls.type.setValue(TableFieldType.INTEGER);
      itemForm.controls.type.disable();
    } else {
      if (+selAggr === FormTableAggregateType.AVG) {
        itemForm.controls.type.setValue(TableFieldType.DECIMAL);
        itemForm.controls.type.disable();
      } else {
        itemForm.controls.type.enable();
      }
    }
    itemForm.controls.columnKey.setValue(prefix + colName);
    itemForm.controls.type.updateValueAndValidity();
  }

  onDefaultProductCategoryChange(categDefValueUuid: string) {
    const i = this.productCategories.findIndex(
      (pcs) => pcs.uuid === categDefValueUuid,
    );
    if (i !== -1) {
      // TODO: add product category attribs as columns (readonly!) if needed
    }
  }

  openConditionDialog() {
    const itemForm = this.itemForm();
    if (itemForm.controls.type.value === +TableFieldType.CONDITION) {
      let data: TableFieldConditionDialogComponent | null = null;
      if ((itemForm.controls.values as FormArray).length > 0) {
        const b64Condition = (itemForm.controls.values as FormArray).controls[0]
          .value;
        const dataJson = Buffer.from(b64Condition, "base64").toString("utf-8");
        data = JSON.parse(dataJson) as TableFieldConditionDialogComponent;
      }
      const dialogRef = this.dialog.open(TableFieldConditionDialogComponent, {
        width: "auto",
        data,
      });
      dialogRef.afterClosed().subscribe((dt: TableFieldCondition) => {
        if (dt != null) {
          const dtJson = JSON.stringify(dt);
          const base64Condition = Buffer.from(dtJson, "utf-8").toString(
            "base64",
          );
          console.log(base64Condition);
          const array = this.itemForm().controls.values as FormArray;
          array.clear();
          array.push(new FormControl(base64Condition));
        }
      });
    }
  }

  protected editValues(): void {
    const valuesArray = this.itemForm().get("values") as FormArray<
      FormControl<string | null>
    >;
    const valueMapControl = this.itemForm().get("valueMap");
    const defaultValueControl = this.itemForm().get("defaultValue");

    let parsedValueMap: Record<string, number | null> = {};

    try {
      parsedValueMap = valueMapControl?.value
        ? JSON.parse(valueMapControl.value)
        : {};
    } catch {
      parsedValueMap = {};
    }

    const currentValues: DropdownValueItem[] = valuesArray.controls.map(
      (control) => {
        const value = control.value ?? "";

        return {
          value,
          numericValue: parsedValueMap[value] ?? null,
        };
      },
    );

    const dialogRef = this.dialog.open<
      DropdownEditValuesDialogComponent,
      { values: DropdownValueItem[] },
      DropdownValueItem[] | undefined
    >(DropdownEditValuesDialogComponent, {
      width: "680px",
      maxWidth: "95vw",
      data: {
        values: currentValues,
        numericValueDisabled: this.selectedType === +TableFieldType.RADIO,
      } as DropdownEditValuesDialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      valuesArray.clear();

      const nextValueMap: Record<string, number | null> = {};

      result.forEach((item) => {
        valuesArray.push(new FormControl(item.value));
        nextValueMap[item.value] = item.numericValue;
      });

      valueMapControl?.setValue(JSON.stringify(nextValueMap));

      const currentDefaultValue = defaultValueControl?.value;

      if (!defaultValueControl) {
        return;
      }

      if (
        currentDefaultValue === null ||
        currentDefaultValue === undefined ||
        currentDefaultValue === ""
      ) {
        return;
      }

      const defaultStillExists = result.some(
        (item) => item.value === currentDefaultValue,
      );

      if (defaultStillExists) {
        return;
      }

      defaultValueControl.setValue(result[0]?.value ?? null);
    });
  }
}
